import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Cancel as CloseIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  NetworkCheck as NetworkIcon,
  InfoOutlined as InfoIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import httpClient from '../services/httpClient';
import AutoApprovalGuideDialog from './AutoApprovalGuideDialog';

const inlineActionButtonSx = {
  flexShrink: 0,
  whiteSpace: 'nowrap',
  minWidth: 'max-content',
};

function SettingSwitch({ label, checked, onChange, tip }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 42 }}>
      <FormControlLabel
        sx={{ m: 0, alignItems: 'center' }}
        control={<Switch checked={!!checked} onChange={onChange} />}
        label={label}
      />
      <Tooltip
        title={tip}
        arrow
        placement="top"
        slotProps={{ tooltip: { sx: { maxWidth: 340, fontSize: '0.8rem' } } }}
      >
        <InfoIcon
          fontSize="small"
          color="action"
          aria-label={`About: ${label}`}
          sx={{ cursor: 'help' }}
        />
      </Tooltip>
    </Box>
  );
}

/**
 * Component for configuring auto-approval settings and email configuration
 */
function AutoApprovalSettings() {
  const [settings, setSettings] = useState({
    enabled: false,
    ipWhitelist: [],
    networkRanges: [],
    autoApproveWithInvitation: true,
    requireAdminApproval: true,
    notificationEmail: '',
    notificationEnabled: true,
    sendgridApiKey: '',
    fromEmail: ''
  });
  
  const [newIp, setNewIp] = useState('');
  const [newRange, setNewRange] = useState('');
  const [testIp, setTestIp] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailMessage, setTestEmailMessage] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await httpClient.get('/v1/admin/auto-approval/settings');
      setSettings(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await httpClient.post('/v1/admin/auto-approval/settings', settings);
      setSuccessMessage('Settings saved successfully');
      await fetchSettings();
    } catch (err) {
      setError('Failed to save settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const addIpToWhitelist = () => {
    if (!newIp.trim()) return;
    
    if (!isValidIp(newIp)) {
      setError('Invalid IP address format');
      return;
    }
    
    if (settings.ipWhitelist.includes(newIp)) {
      setError('IP already in whitelist');
      return;
    }
    
    setSettings({
      ...settings,
      ipWhitelist: [...settings.ipWhitelist, newIp]
    });
    setNewIp('');
    setError(null);
  };

  const removeIpFromWhitelist = (ip) => {
    setSettings({
      ...settings,
      ipWhitelist: settings.ipWhitelist.filter(i => i !== ip)
    });
  };

  const addNetworkRange = () => {
    if (!newRange.trim()) return;
    
    if (!isValidCidr(newRange)) {
      setError('Invalid CIDR format (e.g., 192.168.0.0/16)');
      return;
    }
    
    if (settings.networkRanges.includes(newRange)) {
      setError('Network range already exists');
      return;
    }
    
    setSettings({
      ...settings,
      networkRanges: [...settings.networkRanges, newRange]
    });
    setNewRange('');
    setError(null);
  };

  const removeNetworkRange = (range) => {
    setSettings({
      ...settings,
      networkRanges: settings.networkRanges.filter(r => r !== range)
    });
  };

  const testIpApproval = async () => {
    if (!testIp.trim()) return;
    
    try {
      const response = await httpClient.post('/v1/admin/auto-approval/test', { ip: testIp });
      setTestResult(response.data);
    } catch (err) {
      setError('Failed to test IP: ' + err.message);
    }
  };

  const sendTestEmail = async () => {
    if (!settings.notificationEmail || !settings.notificationEmail.trim()) {
      setTestEmailMessage({ type: 'warning', text: 'Please enter a notification email address first' });
      return;
    }
    
    setTestEmailLoading(true);
    setTestEmailMessage(null);
    
    try {
      await httpClient.post('/v1/auth/test-email', { email: settings.notificationEmail });
      setTestEmailMessage({ type: 'success', text: 'Test email sent successfully! Check your inbox.' });
    } catch (err) {
      setTestEmailMessage({ 
        type: 'error', 
        text: 'Failed to send test email: ' + (err.response?.data?.message || err.message)
      });
    } finally {
      setTestEmailLoading(false);
    }
  };

  const isValidIp = (ip) => {
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(ip)) return false;
    
    const parts = ip.split('.');
    return parts.every(part => parseInt(part) >= 0 && parseInt(part) <= 255);
  };

  const isValidCidr = (cidr) => {
    const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
    if (!cidrRegex.test(cidr)) return false;
    
    const [ip, prefix] = cidr.split('/');
    if (!isValidIp(ip)) return false;
    
    const prefixNum = parseInt(prefix);
    return prefixNum >= 0 && prefixNum <= 32;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon fontSize="large" />
        Email & Auto-Approval Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure email notifications and automatic gateway approval rules
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Email Configuration Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon />
            Email Configuration
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notification Email"
                type="email"
                value={settings.notificationEmail || ''}
                onChange={(e) => setSettings({...settings, notificationEmail: e.target.value})}
                placeholder="admin@example.com"
                helperText="Email address where notifications about new gateway registrations will be sent"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SendGrid API Key"
                type="password"
                value={settings.sendgridApiKey || ''}
                onChange={(e) => setSettings({...settings, sendgridApiKey: e.target.value})}
                placeholder="SG.xxxxxxxxxxxxxxxxxxxxx"
                helperText="Your SendGrid API key for sending email notifications (required for password reset and notification emails)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="From Email Address"
                type="email"
                value={settings.fromEmail || ''}
                onChange={(e) => setSettings({...settings, fromEmail: e.target.value})}
                placeholder="noreply@eyesense.ai"
                helperText="Email address that will appear as the sender of notifications"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button 
                variant="outlined"
                startIcon={testEmailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
                onClick={sendTestEmail}
                disabled={testEmailLoading || !settings.notificationEmail}
              >
                {testEmailLoading ? 'Sending...' : 'Send Test Email'}
              </Button>
              
              {testEmailMessage && (
                <Alert severity={testEmailMessage.type} sx={{ mt: 2 }} onClose={() => setTestEmailMessage(null)}>
                  {testEmailMessage.text}
                </Alert>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notificationEnabled}
                    onChange={(e) => setSettings({...settings, notificationEnabled: e.target.checked})}
                  />
                }
                label="Send email notifications for new registration requests"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* General Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NetworkIcon />
              Auto-Approval Settings
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<HelpOutlineIcon />}
              onClick={() => setGuideOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              How these work
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />
          
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 3,
              rowGap: 1,
            }}
          >
            <SettingSwitch
              label="Enable automatic approval for trusted networks"
              checked={settings.enabled}
              onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
              tip="Master switch for the IP whitelist and network ranges on this page. On: a gateway registering from one of those addresses is approved immediately and skips the pending queue. Off: those lists are ignored, and every registration waits for an admin."
            />
            <SettingSwitch
              label="Automatically approve registrations with valid invitation tokens"
              checked={settings.autoApproveWithInvitation}
              onChange={(e) => setSettings({...settings, autoApproveWithInvitation: e.target.checked})}
              tip="Optional code an admin creates ahead of time for a known installer. The hub does not mint one for a gateway that self-registers. On: presenting that code can skip the pending queue. The gateway stays unclaimed until someone claims its device token."
            />
            <SettingSwitch
              label="Require admin approval even for whitelisted IPs"
              checked={settings.requireAdminApproval}
              onChange={(e) => setSettings({...settings, requireAdminApproval: e.target.checked})}
              tip="Safety lock on the whitelist and network ranges. On: a matching address still waits for you to approve it. Off: a matching address is approved immediately, but only if automatic approval for trusted networks is also on."
            />
          </Box>
        </CardContent>
      </Card>

      <AutoApprovalGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        settings={settings}
      />

      {/* IP Whitelist Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            IP Whitelist
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Specific IP addresses that will be automatically approved (if auto-approval is enabled)
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="192.168.1.100"
              value={newIp}
              sx={{ flex: 1, minWidth: 0 }}
              onChange={(e) => setNewIp(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addIpToWhitelist()}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addIpToWhitelist}
              sx={inlineActionButtonSx}
            >
              Add IP
            </Button>
          </Box>
          
          {settings.ipWhitelist.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>IP Address</TableCell>
                    <TableCell align="right" width="100">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings.ipWhitelist.map((ip) => (
                    <TableRow key={ip}>
                      <TableCell>
                        <Chip label={ip} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeIpFromWhitelist(ip)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No IP addresses in whitelist</Alert>
          )}
        </CardContent>
      </Card>

      {/* Network Ranges Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Network Ranges
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            CIDR network ranges that will be automatically approved (e.g., 192.168.0.0/16)
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="192.168.0.0/16"
              value={newRange}
              sx={{ flex: 1, minWidth: 0 }}
              onChange={(e) => setNewRange(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addNetworkRange()}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addNetworkRange}
              sx={inlineActionButtonSx}
            >
              Add Range
            </Button>
          </Box>
          
          {settings.networkRanges.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>CIDR Range</TableCell>
                    <TableCell align="right" width="100">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings.networkRanges.map((range) => (
                    <TableRow key={range}>
                      <TableCell>
                        <Chip label={range} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeNetworkRange(range)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No network ranges configured</Alert>
          )}
        </CardContent>
      </Card>

      {/* Test Auto-Approval Rules Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Auto-Approval Rules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test if an IP address would be automatically approved based on current settings
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="192.168.1.100"
              value={testIp}
              sx={{ flex: 1, minWidth: 0 }}
              onChange={(e) => setTestIp(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && testIpApproval()}
            />
            <Button
              variant="outlined"
              onClick={testIpApproval}
              sx={inlineActionButtonSx}
            >
              Test IP
            </Button>
          </Box>
          
          {testResult && (
            <Alert 
              severity={testResult.wouldAutoApprove ? 'success' : 'warning'}
              icon={testResult.wouldAutoApprove ? <CheckIcon /> : <CloseIcon />}
            >
              <strong>{testResult.wouldAutoApprove ? 'Would Auto-Approve: ' : 'Manual Approval Required: '}</strong>
              {testResult.message}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
}

export default AutoApprovalSettings;
